/**
 * RTK Query API slice.
 *
 * Uses `fakeBaseQuery` + `queryFn` so every endpoint wraps an existing
 * `shared/api/*` function — no duplicate HTTP logic, full cache/dedup/polling.
 *
 * Add the reducer and middleware to the store (see store.ts).
 *
 * Usage (read):
 *   const { data, isLoading, isError } = useGalleryCatalogsQuery();
 *   const { data } = useNewsByCategoryQuery({ all: 1, category: NEWS_CATEGORY_MARKETING },
 *                                            { pollingInterval: 300_000 });
 *   const { data } = useNewsFromIdQuery(newsId);
 *   const { data } = useShopListQuery({ userID, categoryID: 5 });
 *   const { data } = useShopItemByIdQuery({ postID: id, categoryID: 5 });
 *   const { data } = useOrdersListQuery({ userId, status, sort, start, limit });
 *   const { data } = useMerchListQuery(userCode);
 */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getGalleryCatalogs, getGalleryCatalogsByID } from './gallery';
import { getNews, getNewsFromID, type GetNewsRequestOptions } from './news';
import { getHolidayById } from './holidays';
import type { GalleryCatalogData } from '../types/gallery';
import type { NewsPost } from '../types/news';
import {
  getShopList,
  getShopItemById,
  type GetShopListArgs,
} from './shop/catalog';
import type { ShopItem } from '../types/shop';
import {
  getOrdersList,
  getMerchList,
  manualAddMerch,
  deleteMerch,
} from './shop/orders';
import type { EntityId, OrderStatus } from '../types/common';
import type { MerchListItem, OrderApiMap } from '../types/orders';
import type { ApiStatusResponse } from '../types/orders';
import { getCoins, changeCoins } from './shop/coins';
import { getLogs } from './logs';
import type { GetLogsArgs, LogsPage } from '../types/logs';
import {
  fetchMerchBuyers,
  type MerchBuyersArgs,
  type MerchBuyersReport,
} from './merch';
import {
  createProposal,
  getProposalUsers,
  getProposalsAdminList,
  moderateProposal,
} from './shop/coinProposals';
import type {
  CreateProposalArgs,
  ModerateArgs,
  ProposalRow,
  ProposalUser,
} from '../types/coinProposals';
import { getMassRewardUsers, sendMassReward } from './shop/massReward';
import type {
  MassRewardSendResponse,
  MassRewardUser,
  SendMassRewardArgs,
} from '../types/massReward';
import { getNewsList, type GetNewsListParams } from './newsFeed';
import {
  createTask,
  getTaskFormData,
  getTaskReport,
  getTaskReviews,
  getUserTasks,
  processTaskReview,
  submitTaskProof,
} from './tasks';
import type {
  CreateTaskArgs,
  CreateTaskResponse,
  GetTaskReviewsArgs,
  ProcessTaskReviewArgs,
  SubmitTaskProofArgs,
  TaskActionResponse,
  TaskFormData,
  TaskReportRow,
  TaskReviewRow,
  UserTasks,
} from '../types/tasks';

// ---------- Argument shapes ----------

export type GalleryCatalogByIdArg = {
  id: string;
  fileType?: string | null;
};

export type NewsByCategoryArg = {
  /** `all` param in newsout.php (1 for marketing/company lists, 0 for feed). */
  all: number;
  category: number;
  options?: GetNewsRequestOptions;
};

export type ShopItemByIdArg = {
  postID: string | number;
  categoryID?: number;
  debug?: number;
};

export type OrdersListArg = {
  userId: EntityId;
  status?: OrderStatus;
  sort?: 'ASC' | 'DESC';
  start?: number;
  limit?: number;
};

// ---------- Helper ----------

function toRtkError(error: unknown) {
  return {
    error: {
      status: 'CUSTOM_ERROR' as const,
      error: error instanceof Error ? error.message : String(error),
    },
  };
}

/** Readable message from a rejected `.unwrap()` (the `toRtkError` shape) or a thrown value. */
export const getMutationErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (
    error != null &&
    typeof error === 'object' &&
    'error' in error &&
    typeof error.error === 'string'
  ) {
    return error.error;
  }
  return String(error);
};

// ---------- Slice ----------

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'Gallery',
    'News',
    'Shop',
    'Orders',
    'Merch',
    'Coins',
    'CoinProposals',
    'Tasks',
    'TaskReviews',
    'UserTasks',
  ],
  endpoints: (builder) => ({
    // --- Gallery ---
    galleryCatalogs: builder.query<GalleryCatalogData[], void>({
      queryFn: async () => {
        try {
          return { data: await getGalleryCatalogs() };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['Gallery'],
    }),

    galleryCatalogById: builder.query<
      GalleryCatalogData,
      GalleryCatalogByIdArg
    >({
      queryFn: async ({ id, fileType }) => {
        try {
          return { data: await getGalleryCatalogsByID(id, fileType ?? null) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, { id }) => [{ type: 'Gallery' as const, id }],
    }),

    // --- News ---
    newsByCategory: builder.query<NewsPost[], NewsByCategoryArg>({
      queryFn: async ({ all, category, options }) => {
        try {
          return { data: await getNews(all, category, options) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['News'],
    }),

    newsFromId: builder.query<NewsPost[], EntityId>({
      queryFn: async (id) => {
        try {
          return { data: await getNewsFromID(id) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, id) => [{ type: 'News' as const, id: String(id) }],
    }),

    holidayById: builder.query<
      NewsPost | null,
      { id: EntityId; categoryId?: number }
    >({
      queryFn: async ({ id, categoryId = 2 }) => {
        try {
          return { data: await getHolidayById(id, categoryId) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, { id }) => [
        { type: 'News' as const, id: `holiday-${id}` },
      ],
    }),

    // --- Shop ---
    shopList: builder.query<ShopItem[], GetShopListArgs>({
      queryFn: async (args) => {
        try {
          return { data: await getShopList(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['Shop'],
    }),

    shopItemById: builder.query<ShopItem | null, ShopItemByIdArg>({
      queryFn: async (args) => {
        try {
          return { data: await getShopItemById(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, { postID }) => [
        { type: 'Shop' as const, id: String(postID) },
      ],
    }),

    // --- Orders ---
    ordersList: builder.query<OrderApiMap, OrdersListArg>({
      queryFn: async ({ userId, status, sort, start, limit }) => {
        try {
          return {
            data: await getOrdersList(userId, { status, sort, start, limit }),
          };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, { userId }) => [
        { type: 'Orders' as const, id: String(userId) },
      ],
    }),

    merch: builder.query<MerchListItem[], string | undefined>({
      queryFn: async (userCode) => {
        try {
          return { data: await getMerchList(userCode) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, userCode) => [
        { type: 'Merch' as const, id: String(userCode) },
      ],
    }),

    merchBuyers: builder.query<MerchBuyersReport, MerchBuyersArgs>({
      queryFn: async (args) => {
        try {
          return { data: await fetchMerchBuyers(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['Merch'],
    }),

    logs: builder.query<LogsPage, GetLogsArgs>({
      queryFn: async (args) => {
        try {
          return { data: await getLogs(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
    }),

    // --- Merch mutations ---
    manualAddMerch: builder.mutation<
      ApiStatusResponse,
      { name: string; usercode: string }
    >({
      queryFn: async ({ name, usercode }) => {
        try {
          return { data: await manualAddMerch(name, usercode) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: (_, __, { usercode }) => [
        { type: 'Merch' as const, id: usercode },
      ],
    }),

    deleteMerch: builder.mutation<
      ApiStatusResponse,
      { order: string; variant: string; userId: string }
    >({
      queryFn: async ({ order, variant }) => {
        try {
          return { data: await deleteMerch(order, variant) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: (_, __, { userId }) => [
        { type: 'Merch' as const, id: userId },
      ],
    }),

    // --- Coins ---
    getUserCoins: builder.query<number, EntityId | undefined>({
      queryFn: async (userId) => {
        try {
          return { data: await getCoins(userId) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: (_, __, userId) => [
        { type: 'Coins' as const, id: String(userId) },
      ],
    }),

    changeCoins: builder.mutation<
      ApiStatusResponse & Record<string, unknown>,
      {
        userID: EntityId;
        adminID: EntityId;
        coins: number;
        comment?: string;
        initId?: EntityId;
      }
    >({
      queryFn: async ({ userID, adminID, coins, comment = '', initId }) => {
        try {
          return {
            data: await changeCoins(userID, adminID, coins, comment, initId),
          };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: (_, __, { userID }) => [
        { type: 'Coins' as const, id: String(userID) },
      ],
    }),

    // --- Coin proposals (Softcoin) ---
    proposalUsers: builder.query<ProposalUser[], void>({
      queryFn: async () => {
        try {
          return { data: await getProposalUsers() };
        } catch (error) {
          return toRtkError(error);
        }
      },
    }),

    proposalsAdminList: builder.query<ProposalRow[], void>({
      queryFn: async () => {
        try {
          return { data: await getProposalsAdminList() };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['CoinProposals'],
    }),

    createProposal: builder.mutation<{ message?: string }, CreateProposalArgs>({
      queryFn: async (args) => {
        try {
          return { data: await createProposal(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['CoinProposals'],
    }),

    moderateProposal: builder.mutation<{ message?: string }, ModerateArgs>({
      queryFn: async (args) => {
        try {
          return { data: await moderateProposal(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['CoinProposals'],
    }),

    // --- Mass reward (Softcoin) ---
    massRewardUsers: builder.query<MassRewardUser[], void>({
      queryFn: async () => {
        try {
          return { data: await getMassRewardUsers() };
        } catch (error) {
          return toRtkError(error);
        }
      },
    }),

    sendMassReward: builder.mutation<
      MassRewardSendResponse,
      SendMassRewardArgs
    >({
      queryFn: async (args) => {
        try {
          return { data: await sendMassReward(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['Coins'],
    }),

    // --- Tasks ---
    taskReport: builder.query<TaskReportRow[], void>({
      queryFn: async () => {
        try {
          return { data: await getTaskReport() };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['Tasks'],
    }),

    taskReviews: builder.query<TaskReviewRow[], GetTaskReviewsArgs | void>({
      queryFn: async (args) => {
        try {
          return { data: await getTaskReviews(args ?? {}) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['TaskReviews'],
    }),

    taskFormData: builder.query<TaskFormData, void>({
      queryFn: async () => {
        try {
          return { data: await getTaskFormData() };
        } catch (error) {
          return toRtkError(error);
        }
      },
    }),

    createTask: builder.mutation<CreateTaskResponse, CreateTaskArgs>({
      queryFn: async (args) => {
        try {
          return { data: await createTask(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['Tasks'],
    }),

    // Approval credits coins and adds a completion to the report.
    processTaskReview: builder.mutation<
      TaskActionResponse,
      ProcessTaskReviewArgs
    >({
      queryFn: async (args) => {
        try {
          return { data: await processTaskReview(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['TaskReviews', 'Tasks', 'UserTasks', 'Coins'],
    }),

    submitTaskProof: builder.mutation<TaskActionResponse, SubmitTaskProofArgs>({
      queryFn: async (args) => {
        try {
          return { data: await submitTaskProof(args) };
        } catch (error) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ['TaskReviews', 'UserTasks'],
    }),

    userTasks: builder.query<UserTasks, void>({
      queryFn: async () => {
        try {
          return { data: await getUserTasks() };
        } catch (error) {
          return toRtkError(error);
        }
      },
      providesTags: ['UserTasks'],
    }),

    // --- Feed news (paginated) ---
    /**
     * Paginated feed endpoint using RTK Query's `merge` option.
     * Append pages by incrementing `fromNumber`; re-fetch resets the cache.
     * Usage: const { data } = useNewsFeedPageQuery({ fromNumber: 0, limitNumber: 20, userID });
     */
    newsFeedPage: builder.query<NewsPost[], GetNewsListParams>({
      queryFn: async (params) => {
        try {
          const result = await getNewsList(params);
          return { data: Array.isArray(result) ? result : [] };
        } catch (error) {
          return toRtkError(error);
        }
      },
      // Accumulate pages: appends new items to the existing cache entry.
      merge: (currentCache, newItems) => {
        currentCache.push(...newItems);
      },
      // Re-fetch when the page offset changes (allows infinite-scroll consumers).
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.fromNumber !== previousArg?.fromNumber,
      providesTags: ['News'],
    }),
  }),
});

export const {
  useGalleryCatalogsQuery,
  useGalleryCatalogByIdQuery,
  useNewsByCategoryQuery,
  useNewsFromIdQuery,
  useHolidayByIdQuery,
  useShopListQuery,
  useShopItemByIdQuery,
  useOrdersListQuery,
  useMerchQuery,
  useMerchBuyersQuery,
  useLogsQuery,
  useManualAddMerchMutation,
  useDeleteMerchMutation,
  useGetUserCoinsQuery,
  useChangeCoinsMutation,
  useProposalUsersQuery,
  useProposalsAdminListQuery,
  useCreateProposalMutation,
  useModerateProposalMutation,
  useMassRewardUsersQuery,
  useSendMassRewardMutation,
  useTaskReportQuery,
  useTaskReviewsQuery,
  useTaskFormDataQuery,
  useCreateTaskMutation,
  useProcessTaskReviewMutation,
  useSubmitTaskProofMutation,
  useUserTasksQuery,
  useNewsFeedPageQuery,
} = apiSlice;
